/**
 * One console, top left, instead of three panels that did not know about each
 * other.
 *
 * The body readout, the hold and the structure report were three separate cards
 * with three separate frames, and two of them were anchored at the same corner —
 * so opening both put one on top of the other. They are one stack now, and the
 * overlap stops being possible rather than being fixed.
 *
 * ── Read top to bottom ──────────────────────────────────────────────────────
 *
 * Where you are, what you are carrying, what the ship has been doing. That order
 * is not arbitrary: the first is a fact about the world, the second a fact about
 * the ship, and the third a history — narrowing from outside to inside to
 * behind, so the eye lands on the most immediate thing first.
 *
 * ── Text, with headers, because it is a console ─────────────────────────────
 *
 * A console prints. Sections are separated by a rule and a label rather than by
 * boxes, everything is monospaced, and content types in a character at a time —
 * see `typing.ts`, which also explains why leaving is delayed.
 */

import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';

import { HUD_BACKING, HUD_DIM, HUD_EDGE, HUD_ORANGE, HUD_TEXT } from './FlightHud';
import { keyLabel } from '@interspacial/shared';

import { SHOW_KEY_HINTS, hudScaled } from './hudScale';

import { subscribeLog, type LogEntry } from './shipLog';
import { useTyping } from './typing';

/** How many log lines the console shows. The store keeps more. */
const LOG_SHOWN = 5;

export interface ConsoleSection {
  id: string;
  /** The header. Printed as typed, so it is part of the effect rather than chrome. */
  title: string;
  /**
   * The typed body.
   *
   * Plain text on purpose — this is what the typewriter walks a character at a
   * time, and it cannot do that to a React tree.
   */
  text: string;
  /** Anything that has to be a control or a graphic, printed under the text. */
  extra?: ReactNode;
  /** False while the section is on its way out. */
  present: boolean;
}

/**
 * One section, which owns its own animation and its own unmounting.
 *
 * Split out because each has to keep typing after the parent has stopped
 * wanting it — a section told to go is still on screen for as long as it takes
 * to take itself back, and a parent that simply dropped it would make the exit
 * animation impossible to see.
 */
function Section({
  section,
  onGone,
}: {
  section: ConsoleSection;
  onGone: (id: string) => void;
}) {
  const title = useTyping(section.title, section.present);
  const body = useTyping(section.text, section.present);

  // Only once both have finished taking themselves back is there nothing left
  // to draw, and only then is it safe to be forgotten.
  const finished = title.gone && body.gone;

  useEffect(() => {
    if (finished) onGone(section.id);
  }, [finished, onGone, section.id]);

  if (finished) return null;

  return (
    <div style={{ paddingBottom: 6 }}>
      <Rule />
      <div
        style={{
          fontSize: 9.5,
          letterSpacing: 1.6,
          textTransform: 'uppercase',
          color: HUD_ORANGE,
          marginBottom: 3,
          minHeight: 12,
        }}
      >
        {title.shown}
        {title.phase === 'typing' && <Caret />}
      </div>

      <div style={{ fontSize: 11.5, lineHeight: 1.45, whiteSpace: 'pre-wrap' }}>
        {body.shown}
        {(body.phase === 'typing' || body.phase === 'erasing') && <Caret />}
      </div>

      {/* Controls and graphics come *after* the typed text and are not animated:
          a button that faded in half-drawn is a button you cannot press yet, and
          the whole point of the hold being here is acting on it quickly. */}
      {section.present && body.phase === 'holding' && section.extra}
    </div>
  );
}

/**
 * The rule between sections, printed rather than drawn.
 *
 * A CSS border is a line the browser draws; this is a line the *console* typed,
 * which is the whole difference between a panel with dividers and a terminal
 * with output. Repeated far past the width and clipped, so it fills whatever the
 * console happens to be without anybody counting characters.
 */
function Rule() {
  return (
    <div
      aria-hidden
      style={{
        color: HUD_EDGE,
        fontSize: 10,
        lineHeight: '10px',
        overflow: 'hidden',
        whiteSpace: 'nowrap',
        userSelect: 'none',
        padding: '5px 0 4px',
      }}
    >
      {'='.repeat(80)}
    </div>
  );
}

/** The printing cursor. Only ever shown while something is actually moving. */
function Caret() {
  return (
    <span
      style={{
        display: 'inline-block',
        width: 6,
        marginLeft: 1,
        borderBottom: `1.5px solid ${HUD_ORANGE}`,
        verticalAlign: 'baseline',
      }}
    />
  );
}

export interface ShipConsoleProps {
  /** Sections in order, top to bottom. Absent ones animate away on their own. */
  sections: readonly ConsoleSection[];
  /** The tube, when the ship's power is going. See `SystemsPower`. */
  power?: React.CSSProperties;
  /**
   * Read the full entry behind a log line, when it has one.
   *
   * Absent means the lines are not offered as links — the console is still a
   * console without it.
   */
  onOpenLesson?: (id: string) => void;
}

export function ShipConsole({ sections, power, onOpenLesson }: ShipConsoleProps) {
  const [log, setLog] = useState<readonly LogEntry[]>([]);
  useEffect(() => subscribeLog(setLog), []);

  /** Whether any line on screen leads anywhere, which is what the hint is for. */
  const readable = log.slice(0, LOG_SHOWN).some((entry) => entry.lesson !== undefined);

  /*
   * Sections that have been withdrawn but have not finished leaving.
   *
   * The caller says what it wants on screen. This remembers what is on its way
   * off, because an exit animation has to outlive the prop that started it —
   * a parent that simply dropped a section would make untyping impossible to
   * see, since there would be nothing left to untype.
   */
  const [leaving, setLeaving] = useState<ConsoleSection[]>([]);

  /** What was on screen last time, so a withdrawal can be spotted. */
  const previous = useRef<ConsoleSection[]>([]);

  useEffect(() => {
    const live = new Set(sections.map((one) => one.id));
    const withdrawn = previous.current.filter((one) => !live.has(one.id));
    previous.current = sections.map((one) => ({ ...one }));

    setLeaving((current) => {
      // Anything the caller wants again is the caller's again: drop our copy
      // rather than animating out something that is back on screen.
      const stillGoing = current.filter((one) => !live.has(one.id));

      const fresh = withdrawn
        .filter((one) => !stillGoing.some((going) => going.id === one.id))
        .map((one) => ({ ...one, present: false }));

      if (fresh.length === 0 && stillGoing.length === current.length) return current;
      return [...stillGoing, ...fresh];
    });
  }, [sections]);

  const forget = useCallback(
    (id: string) => setLeaving((current) => current.filter((one) => one.id !== id)),
    [],
  );

  const visible = [...sections, ...leaving];

  return (
    <div
      style={{
        position: 'absolute',
        top: 16,
        left: 16,
        width: 268,
        maxHeight: '68vh',
        overflowY: 'auto',
        background: HUD_BACKING,
        border: `1px solid ${HUD_EDGE}`,
        color: HUD_TEXT,
        padding: '8px 12px 10px',
        fontFamily: 'inherit',
        ...power,
        // After `power`, because that sets an origin of its own for the tube
        // effect and this panel is pinned to a corner rather than centred.
        ...hudScaled('top left'),
      }}
    >
      {/* The console names itself once, at the top, so the stack underneath does
          not need to explain what it is. */}
      <div
        style={{
          fontSize: 9.5,
          letterSpacing: 2,
          textTransform: 'uppercase',
          color: HUD_DIM,
          paddingBottom: 6,
        }}
      >
        ship console
      </div>

      {visible.map((section) => (
        <Section key={section.id} section={section} onGone={forget} />
      ))}

      {/* ── The log ─────────────────────────────────────────────────────────
          Always last and always present, because a console with nothing to say
          is still a console. Newest first: the thing that just happened is the
          thing being looked for. */}
      <div>
        <Rule />
        <div
          style={{
            fontSize: 9.5,
            letterSpacing: 1.6,
            textTransform: 'uppercase',
            color: HUD_ORANGE,
            marginBottom: 3,
          }}
        >
          autopilot
        </div>

        {log.length === 0 ? (
          <div style={{ fontSize: 11.5, lineHeight: 1.45, color: HUD_DIM }}>Standing by.</div>
        ) : (
          log.slice(0, LOG_SHOWN).map((entry, index) => (
            <LogLine
              key={entry.id}
              entry={entry}
              faded={index > 0}
              newest={index === 0}
              onOpenLesson={onOpenLesson}
            />
          ))
        )}

        {/*
            How to reach the lines that can be pressed, and only while there are
            any.

            The cockpit holds pointer lock, so there is no cursor to click with
            until one is asked for — a line drawn as pressable with no way to
            press it is worse than one that is not offered at all. Shown against
            the log rather than in a manual because this is the moment it is
            true.
        */}
        {readable && onOpenLesson && (
          <div
            style={{
              marginTop: 5,
              fontSize: 9.5,
              letterSpacing: 0.6,
              color: HUD_DIM,
              opacity: 0.85,
            }}
          >
            {SHOW_KEY_HINTS
              ? `hold [${keyLabel('cursor')}] and click a marked line to read it in full`
              : 'tap a marked line to read it in full'}
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * One line of the feed.
 *
 * Only the newest types. The ones below it were already typed when they were
 * new, and replaying that every time a line arrives would make the whole feed
 * rewrite itself on every event.
 */
function LogLine({
  entry,
  faded,
  newest,
  onOpenLesson,
}: {
  entry: LogEntry;
  faded: boolean;
  newest: boolean;
  onOpenLesson?: (id: string) => void;
}) {
  const typed = useTyping(entry.text, true);
  const text = newest ? typed.shown : entry.text;

  const body = (
    <>
      <span style={{ color: HUD_DIM }}>› </span>
      {text}
      {newest && typed.phase === 'typing' && <Caret />}
    </>
  );

  /*
   * A marker, because a dotted underline was not enough to tell one line from
   * another at eleven pixels — and most lines are *not* pressable, so the ones
   * that are have to say so rather than leaving the player to try each.
   */
  const marker = (
    <span
      // Same font size as the line, so it cannot stretch the line box. It reads
      // as a marker because of the colour, not because it is smaller.
      style={{ color: HUD_ORANGE, marginLeft: 4, lineHeight: 'inherit' }}
      aria-hidden
    >
      [?]
    </span>
  );

  /*
   * Shared by both shapes, and that is the point.
   *
   * A pressable line is a `<button>` and a plain one is a `<div>`, so anything
   * left to a default differs between them — and the log is a stack of lines
   * where one being a pixel taller than its neighbours is immediately visible.
   * `lineHeight` is stated in pixels rather than as a ratio for the same reason:
   * a ratio is resolved against each element's own font size, and the marker
   * inside a pressable line is smaller than the text around it.
   */
  const line: React.CSSProperties = {
    // A button brings its own box and its own typeface. Flattened so the two
    // shapes agree — and `fontFamily` rather than the `font` shorthand, which
    // resets *size and line-height too*: written after `fontSize` it silently
    // threw both away and drew every line at the panel's inherited size.
    margin: 0,
    padding: 0,
    border: 0,
    textAlign: 'left',
    background: 'transparent',
    fontFamily: 'inherit',
    letterSpacing: 'inherit',

    // Matched to the section bodies above, which is the other text in this
    // panel — the log is the same voice and should read as the same text.
    fontSize: 11.5,
    lineHeight: 1.45,
    color: faded ? HUD_DIM : HUD_TEXT,
    opacity: faded ? 0.75 : 1,
  };

  if (!entry.lesson || !onOpenLesson) return <div style={line}>{body}</div>;

  /*
   * A line that leads back to its own explanation.
   *
   * The quiet half of a lesson is one sentence, and one sentence is not enough
   * for somebody meeting the system for the first time — or for somebody who
   * turned the loud half off and has never seen the long version at all. So the
   * short line is the way back to the long one.
   *
   * A real button rather than a click handler on a div: it is focusable, it is
   * announced as something you can press, and it works through the cockpit's
   * stand-in cursor, which forwards a click by calling `.click()` on whatever
   * is under it.
   */
  return (
    <button
      type="button"
      onClick={() => onOpenLesson(entry.lesson!)}
      title="Read the full entry"
      style={{
        ...line,
        display: 'block',
        width: '100%',
        cursor: 'pointer',
      }}
    >
      {body}
      {marker}
    </button>
  );
}

